import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { useLang, API, fileUrl } from "@/context/LanguageContext";
import { useSEO } from "@/components/seo";
import { Reveal, SectionHead } from "@/components/motion";

export default function Machines() {
  const { t, lang, pathFor } = useLang();
  useSEO("machines");
  const [machines, setMachines] = useState([]);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/machines`).then((r) => setMachines(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-anthracite-dark pt-16" data-testid="machines-page">
      <section className="border-b border-white/10 bg-anthracite grid-lines-dark">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
          <SectionHead kicker={t.machinesPage.kicker} title={t.machinesPage.title} light testid="machines" />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-slate-400">{t.machinesPage.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
        {[["fertigung", t.machinesPage.sectionCnc], ["druck", t.machinesPage.sectionPrint]].map(([sec, secTitle]) => {
          const list = machines.filter((m) => (m.section || "fertigung") === sec).sort((a, b) => (a.order || 0) - (b.order || 0));
          if (!list.length) return null;
          return (
            <div key={sec} className="mb-16 last:mb-0" data-testid={`machines-group-${sec}`}>
              <Reveal>
                <h2 className="mb-8 border-l-2 border-royal pl-4 font-mono-tech text-sm uppercase tracking-[0.35em] text-royal-soft" data-testid={`machines-section-${sec}`}>
                  {secTitle}
                </h2>
              </Reveal>
              <div className="grid gap-10 lg:grid-cols-2">
          {list.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.08}>
              <article className="group border border-white/10 bg-anthracite" data-testid={`machine-card-${i}`}>
                {m.image_url && (
                  <button
                    onClick={() => setActiveImage(m)}
                    className="clip-frame relative block w-full cursor-zoom-in overflow-hidden"
                    aria-label={`${m.name} – ${lang === "de" ? "Bild vergrößern" : "Ingrandisci immagine"}`}
                    data-testid={`machine-image-${i}`}
                  >
                    <img
                      src={fileUrl(m.image_url)}
                      alt={`${m.name} – ${lang === "de" ? m.type_de : m.type_it}`}
                      className={`h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105 ${m.coming_soon ? "opacity-60 saturate-50" : ""}`}
                      loading="lazy"
                    />
                    {m.coming_soon && (
                      <span className="absolute left-0 top-5 z-10 -rotate-3 bg-royal px-5 py-2 font-mono-tech text-xs font-semibold uppercase tracking-[0.3em] text-white shadow-lg" data-testid={`machine-coming-soon-${i}`}>
                        Coming soon
                      </span>
                    )}
                  </button>
                )}
                <div className="p-8">
                  <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-royal-soft">
                    {lang === "de" ? m.type_de : m.type_it}
                  </p>
                  <h2 className="mt-3 text-2xl font-black uppercase tracking-tight text-white">{m.name}</h2>
                  <p className="mt-4 text-sm leading-relaxed text-slate-400">
                    {lang === "de" ? m.description_de : m.description_it}
                  </p>
                  {m.specs?.length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-mono-tech text-xs uppercase tracking-[0.3em] text-slate-500">{t.machinesPage.specsTitle}</h3>
                      <dl className="mt-4 divide-y divide-white/10 border-y border-white/10">
                        {m.specs.map((spec, j) => (
                          <div key={j} className="flex items-center justify-between gap-4 py-3 text-sm">
                            <dt className="text-slate-400">{lang === "de" ? spec.label_de : spec.label_it}</dt>
                            <dd className="text-right font-medium text-white">{spec.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
              </div>
            </div>
          );
        })}
        <Reveal>
          <p className="mt-12 border-l-2 border-royal pl-4 text-sm italic text-slate-400" data-testid="machines-note">{t.machinesPage.note}</p>
        </Reveal>
        <Reveal>
          <div className="mt-16 flex flex-col items-start justify-between gap-8 border border-white/10 bg-anthracite p-8 lg:flex-row lg:items-center lg:p-12">
            <h2 className="max-w-xl text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{t.home.ctaTitle}</h2>
            <Link to={pathFor("inquiry")} className="group flex shrink-0 items-center gap-3 bg-royal px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-royal-hover" data-testid="machines-cta-button">
              {t.home.ctaButton}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>

      <AnimatePresence>
        {activeImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-anthracite-dark/90 p-4 backdrop-blur-sm"
            onClick={() => setActiveImage(null)}
            data-testid="machine-image-modal"
          >
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ duration: 0.3 }}
              className="relative max-h-[90vh] w-full max-w-5xl overflow-auto border border-white/15 bg-anthracite"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActiveImage(null)}
                className="absolute right-4 top-4 z-10 bg-anthracite-dark/80 p-2 text-white transition-colors hover:bg-royal"
                aria-label={t.galleryPage.close}
                data-testid="machine-image-modal-close"
              >
                <X size={20} />
              </button>
              <img
                src={fileUrl(activeImage.image_url)}
                alt={`${activeImage.name} – ${lang === "de" ? activeImage.type_de : activeImage.type_it}`}
                className="max-h-[80vh] w-full object-contain"
              />
              <div className="p-6">
                <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-royal-soft">
                  {lang === "de" ? activeImage.type_de : activeImage.type_it}
                </p>
                <h3 className="mt-1 text-xl font-black uppercase tracking-tight text-white">{activeImage.name}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
